import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { Cron } from '@modules/common/types';
import { PrivateSlackChannelConfirmationModal } from '@modules/subscribe/components/SubscribeDrawerV2/PrivateSlackChannelConfirmationModal/PrivateSlackChannelConfirmationModal';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { SlackChannelInput } from 'generated/graphql';
import { ReactNode, useState } from 'react';
import { EmailOutboundInput } from '../EmailOutboundInput';
import { SlackOutboundCardInput } from '../SlackOutboundCard/SlackOutboundCardInput';
import { OutboundCardFrequency } from './OutboundFrequencyCard';
import SubscribeDrawerFooter from './SubscribeDrawerFooter';
import SubscribeDrawerHeader from './SubscribeDrawerHeader';

interface SubscribeDrawerContentV2Props {
  onClose: () => void;
  isSubscribed: boolean;
  isSaveButtonDisabled: boolean;
  onSaveSubscription: () => void;
  isSubscribeRequestInProgress: boolean;
  onUnsubscribeButtonClick: () => void;
  isUnsubscribeRequestInProgress: boolean;
  selectedSlackChannels: SlackChannelInput[];
  onSelectedSlackChannelsChange: (slackChannels: SlackChannelInput[]) => void;
  selectedEmails: string[];
  onSelectedEmailsChange: (emails: string[]) => void;
  cronSchedule: Cron | undefined;
  setCronSchedule: (cronValue: Cron) => void;
  savedCronValue: Cron;
  shouldShowRealtimeFrequency?: boolean;
  PDFReportingOptions?: {
    isPDFReportingIncluded?: boolean;
    setIsPDFReportingIncluded?: () => void;
  };
  headerNode: ReactNode;
  removeSubscription?: () => void;
  savedSlackChannels: SlackChannelInput[];
}

export const SubscribeDrawerContentV2 = ({
  onClose,
  isSubscribed,
  isSaveButtonDisabled,
  onSaveSubscription,
  isSubscribeRequestInProgress,
  onUnsubscribeButtonClick,
  isUnsubscribeRequestInProgress,
  selectedSlackChannels,
  onSelectedSlackChannelsChange,
  selectedEmails,
  onSelectedEmailsChange,
  cronSchedule,
  setCronSchedule,
  savedCronValue,
  shouldShowRealtimeFrequency = false,
  PDFReportingOptions,
  headerNode,
  removeSubscription,
  savedSlackChannels,
}: SubscribeDrawerContentV2Props) => {
  const { openModal } = useMuiModalContext();

  const [hasInvalidEmails, setHasInvalidEmails] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');

  const onSelectEmails = (emails: string[], hasInvalidEmails?: boolean) => {
    onSelectedEmailsChange(emails);
    setHasInvalidEmails(hasInvalidEmails ?? false);
  };

  const onSave = () => {
    const newlySelectedPrivateSlackChannels = selectedSlackChannels.filter(
      (channel) =>
        channel.isPrivate &&
        !savedSlackChannels.find(
          (savedChannel) => savedChannel.id === channel.id,
        ),
    );

    const hasSubscribedToNewPrivateSlackChannels =
      newlySelectedPrivateSlackChannels.length > 0;

    if (hasSubscribedToNewPrivateSlackChannels) {
      openModal({
        contentComponent: () => (
          <PrivateSlackChannelConfirmationModal
            channels={newlySelectedPrivateSlackChannels}
            onConfirm={onSaveSubscription}
          />
        ),
        modalProps: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      });
      return;
    }

    onSaveSubscription();
  };

  return (
    <>
      <Stack height="100vh" justifyContent="space-between">
        <Box>
          <SubscribeDrawerHeader
            title={isSubscribed ? 'Update Subscription' : 'Subscribe'}
          />
          {headerNode}
          <Box p={2}>
            <Box>
              <Typography variant="body1">
                Where would you like to receive your report?
              </Typography>
            </Box>
            <Box px={2} py={2}>
              <EmailOutboundInput
                selectedEmails={selectedEmails}
                onSelectedEmailsChange={onSelectEmails}
                errorMessage={emailErrorMessage}
                setErrorMessage={setEmailErrorMessage}
              />
            </Box>
            <Box px={2} py={2}>
              <SlackOutboundCardInput
                selectedSlackChannels={selectedSlackChannels}
                onSelectedSlackChannelsChange={onSelectedSlackChannelsChange}
                removeSubscription={removeSubscription}
                isSubcribed={isSubscribed}
                areEmailOutboundsSelected={selectedEmails.length > 0}
              />
            </Box>
            <Box>
              <Divider sx={{ my: 2, opacity: 0.2 }} />
            </Box>
            <OutboundCardFrequency
              savedCronValue={savedCronValue}
              cronSchedule={cronSchedule}
              setCronSchedule={setCronSchedule}
              shouldShowRealtimeFrequency={shouldShowRealtimeFrequency}
            />
          </Box>
        </Box>
        <SubscribeDrawerFooter
          onCancel={onClose}
          onSave={onSave}
          isSaveRequestInProgress={isSubscribeRequestInProgress}
          isSaveDisabled={isSaveButtonDisabled || hasInvalidEmails}
          selectedEmails={selectedEmails}
          setEmailErrorMessage={setEmailErrorMessage}
          isSubscribed={isSubscribed}
          onRemoveButtonClick={onUnsubscribeButtonClick}
          isRemoveRequestInProgress={isUnsubscribeRequestInProgress}
        />
      </Stack>
    </>
  );
};
